import * as React from "react";
import {
  Map,
  PanelLeft,
  PanelLeftClose,
  Settings2,
  Shield,
  Table,
  TrendingUp,
  Users,
  BarChart3,
  LayoutDashboard,
} from "lucide-react";
import WaveMark from "@/assets/confluence/wave.svg?react";
import { Link } from "react-router-dom";
import { NavMain } from "@/components/nav-main";
import { NavSecondary } from "@/components/nav-secondary";
import { NavUser } from "@/components/nav-user";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  useSidebar,
} from "@/components/ui/sidebar";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import {
  useCompanyUser,
  useHasPermission,
  useCheckPermissions,
  useHasModule,
} from "@/store";

// Base navigation data with permission + module requirements
const baseNavData = {
  navOverview: [
    {
      title: "Overview",
      url: "/",
      icon: LayoutDashboard,
    },
  ],
  navMain: [
    {
      title: "Budgets",
      url: "/budget-data",
      icon: Table,
      permission: "can_view_budgets",
      module: "budgets" as const,
    },
    {
      title: "Models",
      url: "/models",
      icon: TrendingUp,
      permission: "can_view_models",
      module: "financial_modeling" as const,
    },
    {
      title: "Reports",
      url: "/reporting",
      icon: BarChart3,
      permissions: ["can_view_models", "can_manage_models"],
      operator: "OR" as const,
      module: "financial_modeling" as const,
    },
  ],
  navSecurity: [
    {
      title: "Users",
      url: "/users",
      icon: Users,
      permissions: ["can_view_users", "can_manage_users"],
      operator: "OR",
    },
    {
      title: "Security",
      url: "/security",
      icon: Shield,
      permissions: [
        "can_view_access_scopes",
        "can_manage_access_scopes",
        "can_view_roles",
        "can_manage_roles",
      ],
      operator: "OR",
    },
  ],
  navSettings: [
    {
      title: "Dimensions",
      url: "/dimensions",
      icon: Map,
      permission: "can_view_dimensions",
    },
  ],
  navSecondary: [
    {
      title: "General Settings",
      url: "/settings/integrations",
      permissions: ["can_view_company_settings", "can_manage_company_settings"],
      operator: "OR",
      icon: Settings2,
    },
  ],
};

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  // Get company user data from store
  const companyUser = useCompanyUser();

  // Get permission checking functions from store
  const hasPermission = useHasPermission;
  const checkPermissions = useCheckPermissions;
  const hasBudgetsModule = useHasModule("budgets");
  const hasModelingModule = useHasModule("financial_modeling");

  // Filter navigation items and sub-items based on permissions + modules
  const filterNavItems = (items: any[]) => {
    const hasAccess = (item: any): boolean => {
      if (item.module === "budgets" && !hasBudgetsModule) return false;
      if (item.module === "financial_modeling" && !hasModelingModule) {
        return false;
      }

      // Single permission check
      if (item.permission) {
        return hasPermission(item.permission);
      }

      // Multiple permissions check
      if (item.permissions && item.permissions.length > 0) {
        return checkPermissions(item.permissions, item.operator || "AND");
      }

      // No permissions required
      return true;
    };

    return items
      .filter(hasAccess)
      .map((item) => ({
        ...item,
        items: item.items ? item.items.filter(hasAccess) : undefined,
      }))
      .filter((item) => !item.items || item.items.length > 0);
  };

  const filteredNavMain = filterNavItems(baseNavData.navMain);
  const filteredNavSecurity = filterNavItems(baseNavData.navSecurity);
  const filteredNavSettings = filterNavItems(baseNavData.navSettings);

  // Create user data from company user
  const userData = React.useMemo(() => {
    if (!companyUser) {
      return {
        name: "Loading...",
        email: "Loading...",
        avatar: "",
      };
    }

    const fullName = `${companyUser.first_name || ""} ${
      companyUser.last_name || ""
    }`.trim();

    return {
      name: fullName || "User",
      email: companyUser.email,
      avatar: "",
    };
  }, [companyUser]);

  return (
    <Sidebar variant="floating" collapsible="icon" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <div className="flex items-center gap-2 group-data-[collapsible=icon]:justify-center">
              <SidebarMenuButton
                size="lg"
                asChild
                className="flex-1 group-data-[collapsible=icon]:hidden"
              >
                <Link to="/">
                  <div className="flex aspect-square size-8 items-center justify-center rounded-sm bg-sidebar-primary text-sidebar-primary-foreground">
                    <WaveMark className="size-5" />
                  </div>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold lowercase tracking-tightest text-sidebar-foreground">
                      finance flows
                    </span>
                    {/* <span className="truncate font-mono text-meta uppercase tracking-micro text-sidebar-muted">
                      Current × Confluence
                    </span> */}
                  </div>
                </Link>
              </SidebarMenuButton>
              <SidebarCollapseButton />
            </div>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <NavMain items={baseNavData.navOverview} groupLabel="Overview" />
        <NavMain items={filteredNavMain} groupLabel="Data" />
        {filteredNavSecurity.length > 0 && (
          <NavMain items={filteredNavSecurity} groupLabel="Access Control" />
        )}
        {filteredNavSettings.length > 0 && (
          <NavMain items={filteredNavSettings} groupLabel="Settings" />
        )}
        <SidebarSeparator className="mt-auto bg-white/10" />
        <div className="flex items-center group-data-[collapsible=icon]:flex-col group-data-[collapsible=icon]:gap-1">
          <NavSecondary items={baseNavData.navSecondary} className="flex-1" />
          <div className="pr-2 group-data-[collapsible=icon]:pr-0">
            <ThemeToggle />
          </div>
        </div>
      </SidebarContent>
      <SidebarFooter>
        <SidebarSeparator className="bg-white/10" />
        <NavUser user={userData} />
      </SidebarFooter>
    </Sidebar>
  );
}

// Integrated sidebar collapse button component
function SidebarCollapseButton() {
  const { toggleSidebar, isMobile, state } = useSidebar();

  if (isMobile) return null;

  const Icon = state === "expanded" ? PanelLeftClose : PanelLeft;

  return (
    <button
      type="button"
      onClick={toggleSidebar}
      className="shrink-0 text-sidebar-foreground opacity-80 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring"
      title={state === "expanded" ? "Collapse sidebar" : "Expand sidebar"}
    >
      <Icon className="h-4 w-4" />
      <span className="sr-only">Toggle Sidebar</span>
    </button>
  );
}
